<template>
  <div class="referral">
    <div class="hero-glow" aria-hidden="true"></div>
    <div class="lp-container referral-inner">
      <div class="lp-eyebrow"><span class="lp-eyebrow__dot"></span>{{ $t('referral.badge') }}</div>
      <h1 class="lp-h1 lp-balance">{{ $t('referral.title') }}</h1>
      <p class="lp-lead lp-balance referral-lead">{{ $t('referral.subtitle') }}</p>

      <!-- El código: lo que el invitado tendrá que pegar en la app -->
      <div v-if="code" class="referral-code">
        <span class="referral-code__label">{{ $t('referral.codeLabel') }}</span>
        <div class="referral-code__row">
          <code class="referral-code__value">{{ code }}</code>
          <button type="button" class="lp-btn lp-btn--ghost" @click="copy">
            {{ copied ? $t('referral.copied') : $t('referral.copy') }}
          </button>
        </div>
      </div>

      <div class="hero-actions">
        <router-link class="lp-btn lp-btn--primary lp-btn--lg" :to="localePath('/download')">
          {{ $t('referral.ctaDownload') }}
        </router-link>
        <router-link class="lp-btn lp-btn--ghost lp-btn--lg" :to="localePath('/login')">
          {{ $t('referral.ctaAccount') }}
        </router-link>
      </div>

      <!-- Quien ya tiene la app: el deep link le lleva el código, sin teclear -->
      <p v-if="code" class="referral-open">
        <button type="button" class="auth-link referral-open__btn" @click="openApp">
          {{ $t('referral.ctaOpenApp') }}
        </button>
      </p>

      <ol class="referral-steps">
        <li>{{ $t('referral.step1') }}</li>
        <li>{{ $t('referral.step2') }}</li>
        <li>{{ $t('referral.step3') }}</li>
      </ol>

      <p class="referral-note">{{ $t('referral.note') }}</p>
    </div>
  </div>
</template>

<script>
import { normalizeReferralCode, saveReferralCode } from '@/services/referral';

/**
 * Página del enlace de invitación (`/r/:code`). Guarda el código en el navegador
 * —para que el alta hecha aquí mismo lo envíe sola— y lo enseña bien grande,
 * porque quien se registre desde la app de escritorio tendrá que teclearlo.
 */
export default {
  name: 'ReferralLanding',
  metaInfo() {
    return {
      title: this.$t('referral.title'),
      titleTemplate: '%s | Nukli',
      meta: [
        { name: 'description', content: this.$t('referral.subtitle') },
        // Un enlace personal no aporta nada a la búsqueda y no debe indexarse.
        { name: 'robots', content: 'noindex, nofollow' },
      ],
    };
  },
  data() {
    return { code: '', copied: false };
  },
  created() {
    this.code = normalizeReferralCode(this.$route.params.code);
    if (this.code) saveReferralCode(this.code);
  },
  methods: {
    /** Prefija la ruta con el idioma actual, igual que el resto de enlaces. */
    localePath(path) {
      const lang = this.$route.params.lang;
      return lang ? `/${lang}${path}` : path;
    },
    /**
     * Abre la app de escritorio con el código (`nukli://r/CODE`). Si no está
     * instalada no pasa nada visible: el navegador ignora el esquema y quedan
     * los CTA de descarga, que es exactamente lo que necesita ese usuario.
     */
    openApp() {
      window.location.href = `nukli://r/${this.code}`;
    },
    async copy() {
      try {
        await navigator.clipboard.writeText(this.code);
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 2000);
      } catch {
        /* sin permiso de portapapeles: el código está a la vista para copiarlo a mano */
      }
    },
  },
};
</script>

<style scoped>
.referral {
  position: relative;
  min-height: 100vh;
  background: var(--lp-bg);
}

.referral-inner {
  padding: 128px 16px 80px;
  text-align: center;
}

.referral-lead {
  margin: 16px auto 32px;
  max-width: 640px;
}

.referral-code {
  display: inline-flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 32px;
  padding: 20px 24px;
  border: 1px solid var(--lp-line);
  border-radius: 16px;
  background: var(--lp-surface);
}

.referral-code__label {
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--lp-muted);
}

.referral-code__row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
}

.referral-code__value {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--lp-ink);
}

.referral-open {
  margin-top: 16px;
  font-size: 14px;
}

.referral-open__btn {
  color: var(--lp-accent);
  background: none;
  border: none;
  cursor: pointer;
  font: inherit;
}

.referral-steps {
  margin: 40px auto 0;
  max-width: 520px;
  padding-left: 20px;
  text-align: left;
  color: var(--lp-muted);
  line-height: 1.7;
}

.referral-note {
  margin-top: 24px;
  font-size: 14px;
  color: var(--lp-muted);
}
</style>
